import { LocalizedText } from "@/i18n/LocalizedText";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { onboardingGuideAPI, type OnboardingGuideState } from "@/services/api";
import { capturePosthogEvent } from "@/lib/posthog";
import { requestOnboardingHighlight } from "@/lib/onboardingHighlight";
import { Button } from "./ui/button";
import { Check, CheckCheck, ChevronRight, LoaderCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";

type OnboardingGuideCardProps = {
  brandId: string | null;
  currentPath: string;
  onNavigate: (to: string) => void;
};

type GuideStepItem = {
  id:
    | "complete_setup"
    | "running_prompts"
    | "activate_competitors"
    | "review_prompt_results"
    | "boost_cluster";
  title: string;
  description: string;
  done: boolean;
  clickable: boolean;
  toPath: string | null;
  targetId: string | null;
};

type StepVisualStatus = "done" | "inProgress" | "todo";

const HOVER_DEBOUNCE_MS = 100;

const getStatusesPayload = (state: OnboardingGuideState) => ({
  complete_setup: state.steps.completeSetup,
  running_prompts: state.steps.runningPrompts,
  activate_competitors: state.steps.activateCompetitors,
  review_prompt_results: state.steps.reviewPromptResults,
  boost_cluster: state.steps.boostCluster,
});

const buildPath = (
  route: string,
  params: Record<string, string | null | undefined>,
) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (!value) return;
    searchParams.set(key, value);
  });
  const query = searchParams.toString();
  return `/dashboard/${route}${query ? `?${query}` : ""}`;
};

export function OnboardingGuideCard({
  brandId,
  currentPath,
  onNavigate,
}: OnboardingGuideCardProps) {
  const queryClient = useQueryClient();
  const viewedKeyRef = useRef<string>("");
  const previousSystemStatusRef = useRef<{
    completeSetup: boolean;
    runningPrompts: boolean;
  } | null>(null);
  const completedEventKeyRef = useRef<string>("");
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openStepId, setOpenStepId] = useState<string | null>(null);
  const [completionOverlayVisible, setCompletionOverlayVisible] =
    useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["onboardingGuide", brandId],
    queryFn: async () => {
      if (!brandId) return null;
      const res = await onboardingGuideAPI.getState(brandId);
      return res.state;
    },
    enabled: Boolean(brandId),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const state = query.state.data as OnboardingGuideState | null | undefined;
      if (!state?.isVisible) return false;
      if (state.steps.runningPrompts) return false;
      return 10_000;
    },
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!data?.isVisible) return;
    const viewKey = `${data.brandId}:${data.progress.completed}/${data.progress.total}`;
    if (viewedKeyRef.current === viewKey) return;
    viewedKeyRef.current = viewKey;
    capturePosthogEvent("onboarding_guide_viewed", {
      brand_id: data.brandId,
      org_id: data.companyId,
      step_statuses: getStatusesPayload(data),
    });
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const previous = previousSystemStatusRef.current;
    if (!previous) {
      previousSystemStatusRef.current = {
        completeSetup: data.steps.completeSetup,
        runningPrompts: data.steps.runningPrompts,
      };
      return;
    }
    if (!previous.completeSetup && data.steps.completeSetup) {
      capturePosthogEvent("onboarding_guide_step_completed", {
        step_name: "complete_setup",
        completion_source: "system",
      });
    }
    if (!previous.runningPrompts && data.steps.runningPrompts) {
      capturePosthogEvent("onboarding_guide_step_completed", {
        step_name: "running_prompts",
        completion_source: "system",
      });
    }
    previousSystemStatusRef.current = {
      completeSetup: data.steps.completeSetup,
      runningPrompts: data.steps.runningPrompts,
    };
  }, [data]);

  useEffect(() => {
    if (!data?.isCompleted || data.completedOverlaySeen || !brandId) {
      setCompletionOverlayVisible(false);
      return;
    }
    setCompletionOverlayVisible(true);
    const completedKey = `${data.brandId}:${data.completedAt || "na"}`;
    if (completedEventKeyRef.current !== completedKey) {
      completedEventKeyRef.current = completedKey;
      capturePosthogEvent("onboarding_guide_completed", {
        brand_id: data.brandId,
        org_id: data.companyId,
      });
    }
    const timer = setTimeout(async () => {
      try {
        await onboardingGuideAPI.updateState({
          brandId,
          completedOverlaySeen: true,
          dismissed: true,
        });
      } finally {
        setCompletionOverlayVisible(false);
        queryClient.invalidateQueries({
          queryKey: ["onboardingGuide", brandId],
        });
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [brandId, data, queryClient]);

  const steps = useMemo<GuideStepItem[]>(() => {
    if (!data || !brandId) return [];

    const reviewTargetId = data.reviewPromptTargetPromptId
      ? `prompt-row-${data.reviewPromptTargetPromptId}`
      : null;
    const baseParams = { brandId };
    const competitorsPath = buildPath("competitors", {
      ...baseParams,
      tab: "suggested",
      guideTarget: "competitors-suggested-tab",
    });
    const promptsPath = buildPath("prompts", {
      ...baseParams,
      tab: "active",
      guidePromptId: data.reviewPromptTargetPromptId,
      guideTarget: reviewTargetId,
    });
    const boostPath = buildPath("get-cited/strategy", {
      ...baseParams,
      cluster: data.boostTargetClusterId,
      guideTarget: "strategy-tasks-section-anchor",
    });

    return [
      {
        id: "complete_setup",
        title: "Complete Setup",
        description: "Add your brand details and generate clusters and prompts",
        done: data.steps.completeSetup,
        clickable: false,
        toPath: null,
        targetId: null,
      },
      {
        id: "running_prompts",
        title: "Running Prompts",
        description:
          "Waiting for prompt results. Add more prompts to improve actions.",
        done: data.steps.runningPrompts,
        clickable: false,
        toPath: null,
        targetId: null,
      },
      {
        id: "activate_competitors",
        title: "Activate Competitors",
        description: "We auto-added competitors. Review or add more.",
        done: data.steps.activateCompetitors,
        clickable: true,
        toPath: competitorsPath,
        targetId: "competitors-suggested-tab",
      },
      {
        id: "review_prompt_results",
        title: "Review Prompt Results",
        description: "Open a prompt to see AI responses and cited sources",
        done: data.steps.reviewPromptResults,
        clickable: true,
        toPath: promptsPath,
        targetId: reviewTargetId,
      },
      {
        id: "boost_cluster",
        title: "Review Strategy Page",
        description: "See what drives AI recommendations and what to do next",
        done: data.steps.boostCluster,
        clickable: true,
        toPath: boostPath,
        targetId: "strategy-tasks-section-anchor",
      },
    ];
  }, [brandId, data]);

  const focusedStepId = data?.nextStep;
  const defaultOpenStepId = useMemo(() => {
    if (!steps.length) return null;
    const focusedStep = focusedStepId
      ? steps.find((step) => step.id === focusedStepId)
      : null;
    if (focusedStep && !focusedStep.done) {
      return focusedStep.id;
    }
    const firstIncomplete = steps.find((step) => !step.done);
    return firstIncomplete?.id ?? steps[0].id;
  }, [focusedStepId, steps]);

  useEffect(() => {
    setOpenStepId(defaultOpenStepId);
  }, [defaultOpenStepId]);

  const clearHoverTimers = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearHoverTimers();
    };
  }, [clearHoverTimers]);

  const scheduleOpen = useCallback((stepId: string) => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = setTimeout(() => {
      setOpenStepId(stepId);
      hoverTimerRef.current = null;
    }, HOVER_DEBOUNCE_MS);
  }, []);

  const scheduleResetToDefault = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
    }
    leaveTimerRef.current = setTimeout(() => {
      setOpenStepId(defaultOpenStepId);
      leaveTimerRef.current = null;
    }, HOVER_DEBOUNCE_MS);
  }, [defaultOpenStepId]);

  const isExpanded = !isCollapsed;
  const shouldRender = Boolean(data?.isVisible && !isLoading);

  if (!brandId || !shouldRender) {
    return null;
  }

  const handleDismiss = async () => {
    await onboardingGuideAPI.updateState({
      brandId,
      dismissed: true,
    });
    queryClient.invalidateQueries({ queryKey: ["onboardingGuide", brandId] });
  };

  const handleStepClick = (step: GuideStepItem) => {
    if (!step.clickable || !step.toPath || !brandId) {
      setOpenStepId(step.id);
      return;
    }
    capturePosthogEvent("onboarding_guide_step_clicked", {
      step_name: step.id,
      from_path: currentPath,
      to_path: step.toPath,
    });
    const stepPath = step.toPath;
    const navigateToStep = () => {
      if (step.targetId) {
        requestOnboardingHighlight(step.targetId, 10_000);
      }
      onNavigate(stepPath);
    };
    navigateToStep();
  };

  const getVisualStatus = (step: GuideStepItem): StepVisualStatus => {
    if (step.done) return "done";
    if (focusedStepId === step.id) return "inProgress";
    return "todo";
  };

  const firstIncompleteIndex = steps.findIndex((step) => !step.done);
  const progressCutoffIndex =
    firstIncompleteIndex === -1 ? steps.length : firstIncompleteIndex;

  return (
    <div className="bg-[linear-gradient(360deg,#220400_-77.91%,#E33501_100%)] onboarding-guide-card relative rounded-xl p-[2px]">
      <div className="rounded-[inherit] p-1 bg-[#171717]">
        <div className="mb-2 mt-2 flex items-center justify-between gap-2">
          <button
            type="button"
            className="cursor-pointer px-3 text-left text-base font-semibold leading-6 text-[#FAFAFA]"
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            <LocalizedText id="legacy.getting.started.cy2ecm" />
          </button>
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-[#262626] px-2 py-0.5 text-xs font-medium leading-4 text-[#FAFAFA]">
              {data?.progress.completed ?? 0}/{data?.progress.total ?? 5}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="mr-1 h-7 w-7 cursor-pointer text-[#A3A3A3] hover:bg-[#262626] hover:text-[#FAFAFA]"
              onClick={handleDismiss}
            >
              <X size={14} />
            </Button>
          </div>
        </div>

        <div
          className={cn("onboarding-guide-collapse", isExpanded && "open mb-2")}
          aria-hidden={!isExpanded}
        >
          <div className="onboarding-guide-steps space-y-0.5">
            {steps.map((step, index) => {
              const isOpen = openStepId === step.id && !step.done;
              const visualStatus = getVisualStatus(step);
              const shouldShowSpinner =
                step.id === "running_prompts" && !step.done;

              return (
                <div
                  key={step.id}
                  className={cn(
                    "onboarding-guide-step cursor-pointer",
                    isOpen && "is-open",
                    visualStatus === "inProgress" && "is-in-progress",
                  )}
                  style={{ animationDelay: `${index * 35}ms` }}
                  onMouseEnter={() => {
                    if (!step.done) {
                      scheduleOpen(step.id);
                    }
                  }}
                  onMouseLeave={scheduleResetToDefault}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (!step.done) {
                        handleStepClick(step);
                      }
                    }}
                    className={cn(
                      "onboarding-guide-row flex w-full items-center gap-2 px-3 py-1 text-left transition-colors",
                      step.clickable
                        ? "cursor-pointer hover:bg-[#1A1A1A]"
                        : "cursor-default",
                    )}
                  >
                    <span
                      className={cn(
                        "onboarding-guide-status mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border",
                        visualStatus === "done" &&
                          "border-[#FF5E00]/95 bg-[#FF5E00]/95 text-[#0A0A0A]",
                        visualStatus === "inProgress" &&
                          "border-[#FF5E00]/80 bg-[#171717] text-[#FF5E00]",
                        visualStatus === "todo" &&
                          "border-[#737373] bg-[#171717] text-transparent",
                      )}
                    >
                      {visualStatus === "done" ? (
                        <Check size={12} className="status-check-pop" />
                      ) : visualStatus === "inProgress" ? (
                        <span className="onboarding-guide-status-dot" />
                      ) : null}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium leading-5 text-[#FAFAFA]">
                          {step.title}
                        </span>
                        {shouldShowSpinner ? (
                          <LoaderCircle
                            size={16}
                            className="animate-spin text-[#FF5E00]"
                          />
                        ) : step.clickable ? (
                          <ChevronRight size={16} className="text-[#A3A3A3]" />
                        ) : (
                          <span className="h-4 w-4" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  </button>

                  <div
                    className={cn(
                      "onboarding-guide-accordion",
                      isOpen && "open",
                      "flex items-center",
                    )}
                  >
                    <div
                      className={cn(
                        "ml-8 mr-6",
                        step.clickable && "cursor-pointer",
                      )}
                    >
                      {!step.done ? (
                        step.clickable ? (
                          <button
                            type="button"
                            onClick={() => handleStepClick(step)}
                            className="mx-1 cursor-pointer text-left text-xs leading-4 text-[#A3A3A3] transition-colors hover:text-[#D4D4D4]"
                          >
                            {step.description}
                          </button>
                        ) : (
                          <p className="mx-1 text-xs leading-4 text-[#A3A3A3]">
                            {step.description}
                          </p>
                        )
                      ) : null}
                    </div>
                  </div>

                  {index < steps.length - 1 ? (
                    <div
                      className={cn(
                        "onboarding-guide-connector ml-[20px] mt-0.5 h-2 border-l",
                        index < progressCutoffIndex
                          ? "border-solid border-[#FF5E00]/75"
                          : "border-dashed border-[#404040]",
                      )}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {completionOverlayVisible ? (
          <div className="m-1 z-10 flex gap-2 flex-col items-center justify-center pointer-none absolute inset-0 bg-black/85 rounded-lg">
            <CheckCheck size={30} className="text-[#FE5D01]" />
            <div className="text-[#FAFAFA] text-base font-semibold">
              <LocalizedText id="legacy.youre.all.set.2tu50r" />
            </div>
            <div className="mx-6 text-muted-foreground leading-5 text-sm text-center">
              <LocalizedText id="legacy.your.brand.analysis.is.up.and.running.1b23owe" />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
